import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '@justgiving/modal';
import CopyToClipboard from '@justgiving/copy-to-clipboard';
import { useTracking } from '@justgiving/react-tracking';

import { useNotNullableContext } from '../../hooks/useNotNullableContext';
import { PageMetaContext } from '../../context/PageMetaContext';
import parseCurrency from '../../utils/parseCurrency';
import FacebookShare from '../SocialButtons/FacebookShare';
import EmailShare from '../SocialButtons/EmailShare';
import TwitterShare from '../SocialButtons/TwitterShare';
import generateHash from '../../utils/generateHash';

import styles from './SuccessMessage.scss';

type SuccessMessageModalProps = {
  isModalOpen: boolean;
  onCloseModal: () => any;
  messages?: ShareButtonMessage;
  currencyCode: string;
  defaultCurrencyAmount: number;
};

type ShareButtonType = 'default' | 'twitter' | 'email';
type ShareButtonMessage = { [key in ShareButtonType]?: string };

const trackingData = { page_section: 'campaign created success message' };

const SuccessMessageModal: React.FunctionComponent<SuccessMessageModalProps> = ({
  isModalOpen,
  onCloseModal,
  messages,
  currencyCode,
  defaultCurrencyAmount,
}) => {
  const { pageType, pageSlug, currentUrl } = useNotNullableContext(
    PageMetaContext,
  );
  const track = useTracking();
  const [copiedToClipboard, setCopiedToClipboard] = useState(false);
  const { t } = useTranslation('common', { useSuspense: false });

  const defaultMessage =
    messages && messages.default
      ? messages.default
      : t('common.shareMessageFallback');

  const shareURL = `${currentUrl}?invite=true`;
  function copyToClipBoard() {
    track({
      ...trackingData,
      event_value: 'copy link',
    });
    setCopiedToClipboard(true);
  }

  const shareSubject = t(
    'campaign.inviteMessage.subject',
    'Please help support our fundraising campaign on JustGiving',
  );
  const shareMessage = t(
    'campaign.inviteMessage.message',
    'You can help The Demo Charity raise money for this great cause by donating directly to their campaign page - ',
  );
  return (
    <Modal isOpen={isModalOpen} onRequestClose={onCloseModal}>
      <div className="jg-text--center">
        <h1>{t('campaign.shareYourPage')}</h1>
        <p>{t('campaign.letEveryoneKnow')}</p>
        <p>
          <strong>{t('campaign.boostDonations')}</strong>
          <span className="jg-color--dark-orchid">
            {currencyCode && parseCurrency(currencyCode, defaultCurrencyAmount)}
          </span>
        </p>
        <div
          className={`jg-campaign-share-modal-content ${styles.campaignShareModalContent}`}
        >
          <div className="jg-space-mbmd">
            <FacebookShare
              className={`jg-pages-social-button-list__button ${styles.buttonNoMax}`}
              trackingData={trackingData}
            />
          </div>
          <div className="jg-space-mbmd">
            <EmailShare
              subject={shareSubject}
              trackingData={trackingData}
              message={`${t(
                'common.greeting',
                'Hello',
              )},\n\n${shareMessage}\n\n${shareURL}\n\n`}
            >
              {t('common.email')}
            </EmailShare>
          </div>
          <div className="jg-space-mbmd">
            <TwitterShare
              url={encodeURIComponent(
                `${currentUrl}?utm_source=Twitter&utm_medium=${pageType}&utm_content=${pageSlug}&utm_campaign=pfp-tweet&utm_term=${generateHash()}`,
              )}
              message={encodeURIComponent(messages?.twitter || defaultMessage)}
              className="jg-pages-social-button-list__button"
              trackingData={trackingData}
            >
              <span className="jg-sr-only">{t('common.shareOn')}</span>
              {t('common.twitter')}
            </TwitterShare>
          </div>
          <div>
            <CopyToClipboard
              text={shareURL}
              copyMessage={t('common.copyLink')}
              copiedMessage={t('common.linkCopied')}
              copied={copiedToClipboard}
              onCopy={copyToClipBoard}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default SuccessMessageModal;
